import { useState, useEffect, useRef } from 'react';

/**
 * Allows detection of when an element is visible in the viewport. Also allows for specifying how much
 * of the element should be visible before being considered visible in the viewport.
 *
 * @param {React.Ref} ref Ref for the item to observer
 * @param {Object} [options]
 * @param {String} [options.rootMargin] How much of the element (in pixels) to be visible
 * @param {String} [options.onlyOnce] Whether the element should be marked not visible after being visible once.
 * @param {String} [options.renderOnServer] When rendering on the server should the src be output
 */
const useOnScreen = (ref, options = {}) => {
  if (!ref) {
    throw new Error('useOnScreen requires a valid ref!');
  }

  const { rootMargin = '0px', onlyOnce = true, renderOnServer = false } = options;
  const [isIntersecting, setIntersecting] = useState(false);
  const hasBeenViewed = useRef(false);

  useEffect(() => {
    const currentNode = ref.current;
    let observer;

    if ('IntersectionObserver' in window) {
      // eslint-disable-next-line no-undef
      observer = new IntersectionObserver(
        ([entry]) => {
          setIntersecting(entry.isIntersecting);
        },
        {
          rootMargin,
        },
      );

      if (currentNode) {
        observer.observe(currentNode);
      }
    } else {
      setIntersecting(true);
    }

    return () => {
      if (observer) {
        observer.unobserve(currentNode);
      }
    };
  }, [ref, rootMargin]);

  useEffect(() => {
    if (hasBeenViewed.current === false && isIntersecting === true) {
      hasBeenViewed.current = true;
    }
  }, [isIntersecting]);

  if (typeof window === 'undefined') {
    return !!renderOnServer;
  }

  return onlyOnce ? hasBeenViewed.current || isIntersecting : isIntersecting;
};

export default useOnScreen;
